import { useState, useEffect } from 'react';
import AOS from 'aos';
import 'aos/dist/aos.css';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { FaImage, FaFilePdf } from 'react-icons/fa';
import PageFlipViewer from '../PageFlipViewer';
import { NewsCardSkeleton } from '../SkeletonLoader';
import { PublicProtectedBlock } from '../PublicProtectedHtml';

const NewsGrid = () => {
  const [newsItems, setNewsItems] = useState([]);
  const [displayItems, setDisplayItems] = useState([]); // News + Ads combined
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [adSettings, setAdSettings] = useState(null);
  const [advertisements, setAdvertisements] = useState([]);
  const [showPdfViewer, setShowPdfViewer] = useState(false);
  const [selectedPdf, setSelectedPdf] = useState({ url: null, title: null });

  // Calculate ad positions
  const calculateAdPositions = (contentCount, frequency) => {
    if (contentCount === 0 || frequency <= 0) return [];
    const positions = [];
    let currentPosition = frequency;
    while (currentPosition <= contentCount) {
      positions.push(currentPosition - 1); // 0-indexed
      currentPosition += frequency;
    }
    return positions;
  };

  // Insert ads into news array
  const insertAdsIntoContent = (news, ads, positions) => {
    if (!ads || ads.length === 0 || positions.length === 0) return news;
    
    const result = [...news];
    let adIndex = 0;
    
    // Insert ads at calculated positions (in reverse to maintain indices)
    positions.reverse().forEach((pos) => {
      if (pos < result.length && adIndex < ads.length) {
        result.splice(pos, 0, { type: 'advertisement', data: ads[adIndex % ads.length] });
        adIndex++;
      }
    });
    
    return result;
  };

  // Fetch news, ad settings, and ads
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch news
        const newsResponse = await axios.get(`${process.env.REACT_APP_API_URL}/api/v1/news/public`);
        
        if (newsResponse.data.success) {
          const news = newsResponse.data.data.news;
          setNewsItems(news);
          
          // Fetch ad display settings (public endpoint)
          try {
            const settingsResponse = await axios.get(`${process.env.REACT_APP_API_URL}/api/v1/advertisement-display-settings/public`);
            if (settingsResponse.data.success && settingsResponse.data.data.enabledForNews) {
              const settings = settingsResponse.data.data;
              setAdSettings(settings);
              
              // Fetch advertisements
              const adUrl = settings.useFeaturedAds 
                ? `${process.env.REACT_APP_API_URL}/api/v1/advertisements/featured`
                : `${process.env.REACT_APP_API_URL}/api/v1/advertisements/public?active=true`;
              
              const adsResponse = await axios.get(adUrl);
              if (adsResponse.data.success) {
                const ads = settings.useFeaturedAds 
                  ? adsResponse.data.data 
                  : adsResponse.data.data.advertisements || [];
                
                setAdvertisements(ads);
                
                // Calculate positions and insert ads
                const positions = calculateAdPositions(news.length, settings.newsAdFrequency);
                const combined = insertAdsIntoContent(news, ads, positions);
                setDisplayItems(combined);
              } else {
                setDisplayItems(news);
              }
            } else {
              setDisplayItems(news);
            }
          } catch (adErr) {
            console.error('Error fetching ad settings:', adErr);
            setDisplayItems(news);
          }
        } else {
          setError('Failed to fetch news');
        }
      } catch (err) {
        console.error('Error fetching news:', err);
        setError('Error fetching news. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Initialize AOS when component mounts
  useEffect(() => {
    // Initialize AOS safely
    try {
      if (typeof window !== 'undefined' && typeof AOS !== 'undefined') {
        if (!AOS.initialized) {
          AOS.init({
            duration: 800,
            once: false,
            mirror: true,
          });
        } else {
          AOS.refresh();
        }
      }
    } catch (error) {
      console.warn('AOS initialization error:', error);
    }
  }, []);

  // Function to extract the first image from HTML content
  const extractFirstImageFromContent = (content) => {
    if (!content) return null;
    
    const imgRegex = /<img[^>]+src="([^">]+)"/i;
    const match = content.match(imgRegex);
    
    return match ? match[1] : null;
  };

  // Function to strip HTML tags and get plain text
  const stripHtmlTags = (html) => {
    if (!html) return '';
    return html.replace(/<[^>]*>/g, ' ').replace(/\s{2,}/g, ' ').trim();
  };

  // Advertisement Card Component
  const AdvertisementCard = ({ ad, index }) => {
    const imageToShow = ad.imageUrl || extractFirstImageFromContent(ad.content);
    const plainTextContent = stripHtmlTags(ad.content);
    
    return (
      <div 
        data-aos="fade-up"
        data-aos-delay={index * 100}
        data-aos-anchor-placement="center-bottom"
        className="bg-gradient-to-br from-orange-50 to-yellow-50 rounded-lg overflow-hidden shadow-md mb-8 h-full flex flex-col border-2 border-orange-200"
      >
        <PublicProtectedBlock className="flex flex-col flex-grow min-h-0">
          <div className="relative">
            <div className="absolute top-4 left-4 z-10 bg-orange-500 text-white text-xs font-bold px-3 py-1 rounded">
              Advertisement
            </div>
            {imageToShow && (
              <div className="relative h-48 overflow-hidden bg-gray-200 flex items-center justify-center">
                <img 
                  src={imageToShow} 
                  alt={ad.title} 
                  className="w-full h-full object-contain"
                  style={{
                    width: 'auto',
                    height: 'auto',
                    maxWidth: '100%',
                    maxHeight: '100%'
                  }}
                />
              </div>
            )}
          </div>
          
          <div className="p-6 flex-grow flex flex-col">
            <h2 className="text-2xl font-bold mb-3 leading-tight text-gray-800">{ad.title}</h2>
            {plainTextContent && (
              <p className="text-gray-600 mb-4 line-clamp-3">
                {plainTextContent.length > 120 ? `${plainTextContent.substring(0, 120)}...` : plainTextContent}
              </p>
            )}
            
            <div className="flex items-center justify-between border-t pt-4 mt-auto">
              <Link
                to={`/advertisement/${ad.id}`}
                className="text-orange-600 hover:text-orange-800 font-medium text-sm"
              >
                View Details →
              </Link>
              {ad.pdfUrl && (
                <button
                  type="button"
                  onClick={() => {
                    setSelectedPdf({ url: ad.pdfUrl, title: ad.title });
                    setShowPdfViewer(true);
                  }}
                  className="flex items-center text-red-600 hover:text-red-800 text-sm"
                >
                  <FaFilePdf className="mr-1" />
                  PDF
                </button>
              )}
            </div>
          </div>
        </PublicProtectedBlock>
      </div>
    );
  };

  const NewsCard = ({ item, index }) => {
    // Determine which image to display
    let imageToShow = null;
    
    if (item.imageUrl) {
      // Use the dedicated image if it exists
      imageToShow = item.imageUrl;
    } else {
      // Try to extract image from content
      const contentImage = extractFirstImageFromContent(item.content);
      if (contentImage) {
        // Check if it's a data URL or regular URL
        imageToShow = contentImage.startsWith('data:') ? contentImage : contentImage;
      }
    }
    
    // Get plain text content for preview
    const plainTextContent = stripHtmlTags(item.content);
    
    return (
      <div 
        data-aos="fade-up"
        data-aos-delay={index * 100}
        data-aos-anchor-placement="center-bottom"
        className="bg-white rounded-lg overflow-hidden shadow-md mb-8 h-full flex flex-col"
      >
        <Link to={`/news-details?id=${item.id}`} className="flex-grow flex flex-col min-h-0">
          <PublicProtectedBlock className="flex flex-col flex-grow min-h-0">
            <div className="relative h-48 overflow-hidden bg-gray-200 flex items-center justify-center">
              {imageToShow ? (
                <img 
                  src={imageToShow} 
                  alt={item.title} 
                  className="w-full h-full object-contain transition-transform duration-300 hover:scale-105"
                />
              ) : (
                <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                  <FaImage className="text-gray-400 text-5xl" />
                </div>
              )}
              <span className="absolute top-4 left-4 bg-yellow-400 text-xs font-bold px-3 py-1 rounded">
                {item.category}
              </span>
            </div>
            
            <div className="p-6 flex-grow">
              <h2 className="text-2xl font-bold mb-3 leading-tight">{item.title}</h2>
              <p className="text-gray-600 mb-4 line-clamp-3">
                {plainTextContent.length > 120 ? `${plainTextContent.substring(0, 120)}...` : plainTextContent}
              </p>
              
              <div className="flex items-center border-t pt-4 mt-auto">
                <div className="flex items-center text-xs text-gray-500">
                  <span>{new Date(item.createdAt).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}</span>
                  <span className="mx-2">•</span>
                  <span>{Math.ceil(plainTextContent.length / 1000)} Min Read</span>
                </div>
              </div>
            </div>
          </PublicProtectedBlock>
        </Link>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-12 bg-gray-50">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold mb-2">Latest News</h1>
          <p className="text-gray-600 text-lg">Stay informed with our curated selection of top stories</p>
        </div>
        <div className="space-y-6">
          <NewsCardSkeleton count={6} />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-12 bg-gray-50">
        <div className="text-center text-red-500">
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-12 bg-gray-50">
      <div className="text-center mb-12">
        <h1 className="text-4xl font-bold mb-2">Latest News</h1>
        <p className="text-gray-600 text-lg">Stay informed with our curated selection of top stories</p>
      </div>

      {displayItems.length === 0 ? (
        <div className="text-center text-gray-500">
          <p>No news articles available at the moment.</p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {displayItems.map((item, index) => {
              if (item.type === 'advertisement') {
                return (
                  <AdvertisementCard 
                    key={`ad-${item.data.id}-${index}`} 
                    ad={item.data} 
                    index={index} 
                  />
                );
              }
              return (
                <NewsCard 
                  key={item.id} 
                  item={item} 
                  index={index} 
                />
              );
            })}
          </div>
          
          {/* PDF Flipbook Viewer Modal */}
          {showPdfViewer && selectedPdf.url && (
            <PageFlipViewer
              pdfUrl={selectedPdf.url}
              title={selectedPdf.title || "PDF Document"}
              onClose={() => {
                setShowPdfViewer(false);
                setSelectedPdf({ url: null, title: null });
              }}
            />
          )}
        </>
      )}
    </div>
  );
};

export default NewsGrid;